import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FiLayers } from "react-icons/fi";
import apiService from "../services/apiService";
import "./ReorderLandingPage.css";

const ReorderLandingPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Drag and drop states
  const [draggedRow, setDraggedRow] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/properties?isfeatured=false');
      setProperties(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching properties:", err);
      setError("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "for sale":
        return "for-sale";
      case "for rent":
        return "for-rent";
      case "sold":
        return "sold";
      case "under negotiation":
        return "under-negotiation";
      default:
        return "default";
    }
  };

  const getCategoryColor = (category) => {
    switch (category.toLowerCase()) {
      case "office":
        return "office";
      case "industrial":
        return "industrial";
      case "residential":
        return "residential";
      case "commercial":
        return "commercial";
      case "leisure":
        return "leisure";
      default:
        return "default";
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, property, index) => {
    setDraggedRow(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target);
  };

  const handleDragOver = (e, property, index) => {
    if (draggedRow === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget(index);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (e, targetProperty, targetIndex) => {
    e.preventDefault();
    if (draggedRow === null) return;

    const draggedProperty = properties[draggedRow];

    // Don't do anything if dropping on itself
    if (draggedRow === targetIndex) {
      setDraggedRow(null);
      setDropTarget(null);
      return;
    }

    // Get the row immediately above the drop target
    const rowAbove = targetIndex > 0 ? properties[targetIndex - 1] : null;
    const propertyBeforeId = rowAbove ? rowAbove.id : 0;
    
    if (rowAbove) {
      alert(`Row above drop position - ID: ${rowAbove.id}, Name: ${rowAbove.listingTitle}`);
    } else {
      alert('Row above drop position - ID: 0 (dropped at the top)');
    }

    try {
      // Call API to update landing page order
      await apiService.put('/properties/re-order-landing-page', {
        propertyId: draggedProperty.id,
        propertyBeforeId: propertyBeforeId
      });

      // Refresh the list
      await fetchProperties();

    } catch (error) {
      console.error('Error reordering property:', error);
      alert('Failed to reorder property. Please try again.');
    }

    setDraggedRow(null);
    setDropTarget(null);
  };

  const handleDragEnd = () => {
    setDraggedRow(null);
    setDropTarget(null);
  };

  if (loading) {
    return (
      <div className="reorder-landing-page">
        <div className="page-header">
          <h1>Re-order Landing Page</h1>
        </div>
        <div className="loading-state">
          <p>Loading properties...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reorder-landing-page">
        <div className="page-header">
          <h1>Re-order Landing Page</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchProperties} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reorder-landing-page">
      <div className="page-header">
        <h1>Re-order Landing Page</h1>
        <p className="page-description">Drag and drop properties to reorder them on the landing page</p>
      </div>

      <div className="properties-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>Display Order</th>
              <th>Ref</th>
              <th>Property Name</th>
              <th>Category</th>
              <th>Status</th>
              <th>Published</th>
              <th>Discrete List</th>
              <th>Carousel Featured</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((property, index) => (
              <tr 
                key={property.id}
                draggable={true}
                onDragStart={(e) => handleDragStart(e, property, index)}
                onDragOver={(e) => handleDragOver(e, property, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, property, index)}
                onDragEnd={handleDragEnd}
                className={`
                  draggable-row
                  ${draggedRow === index ? 'dragging' : ''}
                  ${dropTarget === index ? 'drop-target' : ''}
                  ${property.placeholderyn === 1 ? 'special-property-row' : ''}
                `}
              >
                <td className="drag-handle-cell">
                  <div className="drag-handle" title="Drag to reorder">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="9" cy="6" r="1.5"/>
                      <circle cx="15" cy="6" r="1.5"/>
                      <circle cx="9" cy="12" r="1.5"/>
                      <circle cx="15" cy="12" r="1.5"/>
                      <circle cx="9" cy="18" r="1.5"/>
                      <circle cx="15" cy="18" r="1.5"/>
                    </svg>
                  </div>
                </td>
                <td>
                  <strong>{property.displayOrder}</strong>
                </td>
                <td>
                  <strong>{property.propertyReference}</strong>
                  {property.portfolioPropertyId && (
                    <FiLayers 
                      style={{ 
                        color: '#4ade80', 
                        fontSize: '18px', 
                        marginLeft: '8px', 
                        verticalAlign: 'middle',
                        display: 'inline-block'
                      }} 
                      title={property.portfolioTitle || "In portfolio"} 
                    />
                  )}
                </td>
                <td>
                  <Link
                    to={`/properties/${property.id}`}
                    className="property-link"
                  >
                    {property.listingTitle}
                    {property.placeholderyn === 1 && <span style={{ color: '#ff6b6b', fontSize: '20px', marginLeft: '8px' }}>*</span>}                    
                  </Link>
                  <p className="property-description">
                    {property.listingDescription}
                  </p>
                </td>
                <td>
                  {property.category}
                </td>
                <td>
                  {property.propertyStatus}
                </td>
                <td>
                  <span
                    className={`active-badge ${
                      property.isActive ? "active" : "inactive"
                    }`}
                  >
                    {property.isActive ? "Yes" : "No"}
                  </span>
                </td>
                <td>
                  <span
                    className={`discrete-badge ${
                      property.isDiscrete ? "discrete" : "not-discrete"
                    }`}
                  >
                    {property.isDiscrete ? "Yes" : "No"}
                  </span>
                </td>
                <td>
                  <span
                    className={`featured-badge ${
                      property.isFeatured ? "featured" : "not-featured"
                    }`}
                  >
                    {property.isFeatured ? "Yes" : "No"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReorderLandingPage;
